<template>
  <div class="grid-view__group-columns">
    <div
      v-if="includeRowDetails"
      class="grid-view__group-columns-spacer"
      :style="{ width: gridViewRowDetailsWidth + 'px' }"
    ></div>
    <div
      v-for="(field, fieldIndex) in visibleFields"
      :key="'group-col-' + field.id"
      class="grid-view__group-columns-cell"
      :class="{
        'grid-view__group-columns-cell--dragging':
          draggingField && draggingField.id === field.id,
      }"
      :style="{ width: fieldWidths[field.id] + 'px' }"
      @contextmenu.prevent.stop="showFieldContext(field, $event)"
      @mousedown="startColumnDrag($event, field, fieldIndex)"
    >
      <!-- Origem Flow: clique no nome ordena a tabela por essa coluna
           (crescente -> decrescente -> sem ordenação, estilo planilha).
           Botão direito continua abrindo o menu do campo (Editar, Ocultar,
           Duplicar, Inserir e Excluir) — o mesmo do cabeçalho original
           herdado do upstream, que some no modo banda. -->
      <span
        class="grid-view__group-columns-cell-name grid-view__group-columns-cell-name--clickable"
        :class="{
          'grid-view__group-columns-cell-name--sorted': !!getFieldSort(field),
        }"
        @click.stop="toggleHeaderSort($event, field)"
        >{{ field.name
        }}<i
          v-if="getFieldSort(field)"
          class="grid-view__group-columns-cell-sort-icon"
          :class="
            getFieldSort(field).order === 'ASC'
              ? 'iconoir-arrow-up'
              : 'iconoir-arrow-down'
          "
        ></i
      ></span>
      <HorizontalResize
        v-if="!readOnly"
        class="grid-view__group-columns-width-handle"
        :width="fieldWidths[field.id]"
        :min="GRID_VIEW_MIN_FIELD_WIDTH"
        @move="moveFieldWidth(field, $event)"
        @update="updateFieldWidth(field, view, database, readOnly, $event)"
      ></HorizontalResize>
    </div>
    <!-- Origem Flow: como no ClickUp, o "+" do cabeçalho de colunas cria
         uma COLUNA (as tarefas entram pelo "+ Adicionar tarefa" do grupo).
         No modo banda o cabeçalho global do grid some, então este é o
         único caminho para adicionar campo. -->
    <a
      v-if="!readOnly"
      ref="addFieldLink"
      class="grid-view__group-columns-add"
      @click.stop="showCreateFieldContext($event)"
    >
      <i class="iconoir-plus"></i>
      {{ $t('gridView.addInGroupHeader') }}
    </a>
    <CreateFieldContext
      v-if="!readOnly && database"
      ref="createFieldContext"
      :table="view.table"
      :view="view"
      :all-fields-in-table="allFieldsInTable"
      :database="database"
      @field-created="$emit('field-created', $event)"
      @field-created-callback-done="afterFieldCreatedUpdateFieldOptions"
    ></CreateFieldContext>
    <!-- Origem Flow: indicador da posição de destino ao arrastar uma
         coluna pelo cabeçalho do grupo (modo banda). -->
    <div
      v-show="draggingField"
      class="grid-view__group-columns-drag-indicator"
      :style="{ left: dragIndicatorLeft + 'px' }"
    ></div>
    <FieldContext
      v-if="fieldContextRendered && database && contextField"
      ref="fieldContext"
      :database="database"
      :table="view.table"
      :view="view"
      :field="contextField"
      :all-fields-in-table="allFieldsInTable"
      @update="$emit('field-updated', $event)"
      @delete="$emit('field-deleted')"
    ></FieldContext>
  </div>
</template>

<script>
import HorizontalResize from '@baserow/modules/core/components/HorizontalResize'
import CreateFieldContext from '@baserow/modules/database/components/field/CreateFieldContext'
import FieldContext from '@baserow/modules/database/components/field/FieldContext'
import gridViewHelpers from '@baserow/modules/database/mixins/gridViewHelpers'
import viewHelpers from '@baserow/modules/database/mixins/viewHelpers'
import { notifyIf } from '@baserow/modules/core/utils/error'
import { DEFAULT_SORT_TYPE_KEY } from '@baserow/modules/database/constants'

/**
 * Origem Flow: cabeçalho de colunas POR GRUPO (estilo ClickUp) — no modo
 * banda o cabeçalho global do grid é escondido e cada grupo repete os nomes
 * das colunas, com o "Adicionar" verde à direita que cria uma tarefa já no
 * grupo. As alças de resize reutilizam o mesmo fluxo do cabeçalho original.
 */
export default {
  name: 'GridViewGroupColumnsHeader',
  components: { HorizontalResize, CreateFieldContext, FieldContext },
  mixins: [gridViewHelpers, viewHelpers],
  props: {
    view: {
      type: Object,
      required: true,
    },
    database: {
      type: Object,
      required: false,
      default: null,
    },
    visibleFields: {
      type: Array,
      required: true,
    },
    fieldWidths: {
      type: Object,
      required: true,
    },
    includeRowDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Payload { groupBy, field, value } do grupo, repassado no evento
     * add-row-in-group quando o usuário clica em "Adicionar".
     */
    group: {
      type: Object,
      required: true,
    },
    allFieldsInTable: {
      type: Array,
      required: false,
      default: () => [],
    },
    canAddRow: {
      type: Boolean,
      required: false,
      default: false,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [
    'add-row-in-group',
    'field-created',
    'field-updated',
    'field-deleted',
    'refresh',
  ],
  data() {
    return {
      fieldContextRendered: false,
      contextField: null,
      // Origem Flow: estado do arrasto de coluna no cabeçalho do grupo.
      draggingField: null,
      dragCandidate: null,
      dragMoved: false,
      dragStartX: 0,
      dragTargetIndex: null,
      dragIndicatorLeft: 0,
    }
  },
  beforeUnmount() {
    this.stopColumnDragListeners()
  },
  methods: {
    /**
     * Origem Flow: arrastar o cabeçalho de uma coluna troca a coluna de
     * lugar (linha 12 da tabela de ajustes). No modo banda o cabeçalho
     * global (que tem o arrasto do upstream) fica oculto, então o arrasto
     * vive aqui e persiste pela mesma action do upstream
     * (updateSingleFieldOptionOrder). Um movimento menor que 3px continua
     * valendo como clique (ordenar pela coluna).
     */
    startColumnDrag(event, field, fieldIndex) {
      if (this.readOnly || event.button !== 0) {
        return
      }
      // Sem isto o arrasto seleciona o texto dos cabeçalhos (o click do
      // toggle de ordenação não é afetado).
      event.preventDefault()
      this.dragCandidate = { field, index: fieldIndex }
      this.dragStartX = event.clientX
      this.dragMoved = false
      this.dragMoveEvent = (e) => this.columnDragMove(e)
      this.dragUpEvent = (e) => this.columnDragUp(e)
      window.addEventListener('mousemove', this.dragMoveEvent)
      window.addEventListener('mouseup', this.dragUpEvent)
    },
    stopColumnDragListeners() {
      document.body.classList.remove('sortable-dragging')
      window.removeEventListener('mousemove', this.dragMoveEvent)
      window.removeEventListener('mouseup', this.dragUpEvent)
    },
    columnDragMove(event) {
      if (!this.dragMoved) {
        if (Math.abs(event.clientX - this.dragStartX) <= 3) {
          return
        }
        this.dragMoved = true
        this.draggingField = this.dragCandidate.field
        // Cursor "grabbing" global, como nos demais motores de arrasto.
        document.body.classList.add('sortable-dragging')
      }
      event.preventDefault()
      const cells = this.$el.querySelectorAll('.grid-view__group-columns-cell')
      if (cells.length === 0) {
        return
      }
      const rootRect = this.$el.getBoundingClientRect()
      let target = cells.length
      let left = null
      for (let i = 0; i < cells.length; i++) {
        const rect = cells[i].getBoundingClientRect()
        if (event.clientX < rect.left + rect.width / 2) {
          target = i
          left = rect.left - rootRect.left
          break
        }
      }
      if (left === null) {
        const last = cells[cells.length - 1].getBoundingClientRect()
        left = last.right - rootRect.left
      }
      this.dragTargetIndex = target
      this.dragIndicatorLeft = left
    },
    async columnDragUp() {
      this.stopColumnDragListeners()
      const candidate = this.dragCandidate
      const moved = this.dragMoved
      const target = this.dragTargetIndex
      this.dragCandidate = null
      this.dragMoved = false
      this.draggingField = null
      this.dragTargetIndex = null
      if (!moved || !candidate || target === null) {
        return
      }
      // Suprime o clique que dispara logo após soltar (senão a coluna
      // também seria ordenada).
      const suppress = (clickEvent) => {
        clickEvent.stopPropagation()
        window.removeEventListener('click', suppress, true)
      }
      window.addEventListener('click', suppress, true)
      setTimeout(() => window.removeEventListener('click', suppress, true))

      const fields = this.visibleFields
      const from = candidate.index
      // Soltar no mesmo lugar (antes ou depois de si mesma) não muda nada.
      if (target === from || target === from + 1) {
        return
      }
      let position
      let fromField
      if (target === 0) {
        position = 'left'
        fromField = { id: fields[0].id }
      } else {
        position = 'right'
        fromField = { id: fields[target - 1].id }
      }
      if (fromField.id === candidate.field.id) {
        return
      }
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/grid/updateSingleFieldOptionOrder',
          {
            fieldToMove: candidate.field,
            position,
            fromField,
            readOnly: this.readOnly,
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    getFieldSort(field) {
      return this.view.sortings.find((sort) => sort.field === field.id)
    },
    canSortFieldFromHeader(field) {
      return (
        !this.readOnly &&
        this.$registry.get('field', field.type).getCanSortInView(field) &&
        this.database &&
        this.$hasPermission(
          'database.table.view.create_sort',
          this.view,
          this.database.workspace.id
        )
      )
    },
    /**
     * Origem Flow: "Prioridade" foi criada com as opções já na ordem
     * semântica correta (Urgente, Alta, Média, Baixa) — ordenar por essa
     * posição em vez de alfabética evita "Alta, Baixa, Média, Urgente".
     */
    getDefaultSortTypeForField(field) {
      const sortTypes = this.$registry.get('field', field.type).getSortTypes(field)
      const name = (field.name || '').trim().toLowerCase()
      if (
        name === 'prioridade' &&
        Object.prototype.hasOwnProperty.call(sortTypes, 'order')
      ) {
        return 'order'
      }
      return DEFAULT_SORT_TYPE_KEY
    },
    async toggleHeaderSort(event, field) {
      if (!this.canSortFieldFromHeader(field)) {
        return
      }

      const sort = this.getFieldSort(field)

      try {
        if (sort === undefined) {
          await this.$store.dispatch('view/createSort', {
            view: this.view,
            field,
            values: {
              field: field.id,
              order: 'ASC',
              type: this.getDefaultSortTypeForField(field),
            },
          })
        } else if (sort.order === 'ASC') {
          await this.$store.dispatch('view/updateSort', {
            sort,
            values: { field: field.id, order: 'DESC' },
          })
        } else {
          await this.$store.dispatch('view/deleteSort', {
            view: this.view,
            sort,
          })
        }

        this.$emit('refresh')
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    showFieldContext(field, event) {
      if (this.readOnly) {
        return
      }
      const target = event.currentTarget
      this.fieldContextRendered = true
      this.contextField = field
      this.$nextTick(() => {
        const context = this.$refs.fieldContext
        if (context) {
          context.toggle(target, 'bottom', 'left', 4)
        }
      })
    },
    showCreateFieldContext(event) {
      const context = this.$refs.createFieldContext
      if (!context) {
        return
      }
      context.toggle(event.currentTarget, 'bottom', 'right', 4)
      this.$nextTick(() => {
        context.showFieldTypesDropdown(event.currentTarget)
      })
    },
  },
}
</script>
